import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, Star, ArrowRight, X, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

export const PricingSection = () => {
  const navigate = useNavigate();
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  
  const plans = [{
    name: "Free",
    price: "$0",
    period: "/month",
    description: "Perfect for users or small teams getting started",
    features: ["Up to 1 owned workspace", "3 users per workspace", "1,000 tasks per workspace", "Basic queue management", "Email support"],
    cta: "Sign Up",
    variant: "outline" as const
  }, {
    name: "Starter",
    price: "$10",
    period: "/month",
    description: "For growing teams that need more power",
    features: ["Up to 3 owned workspaces", "5 users per workspace", "5,000 tasks per workspace", "Advanced analytics", "Custom public forms", "Priority tickets", "API access*"],
    cta: "Sign Up",
    variant: "gradient" as const,
    popular: true
  }, {
    name: "Professional",
    price: "$35",
    period: "/month",
    description: "For large teams with specific needs",
    features: ["Up to 5 owned workspaces", "10 users per workspace", "10,000 tasks per workspace", "Dedicated support", "SSO integration*", "Advanced security", "Custom development"],
    cta: "Sign Up",
    variant: "outline" as const
  }];

  const allFeatures = [
    { name: "Workspaces", free: "1", starter: "3", pro: "5" },
    { name: "Users", free: "3", starter: "5", pro: "10" },
    { name: "Tasks", free: "1,000", starter: "5,000", pro: "10,000" },
    { name: "Queue Management", free: "Basic", starter: "Advanced", pro: "Advanced" },
    { name: "Analytics", free: "Basic", starter: "Advanced", pro: "Advanced" },
    { name: "Support", free: "Email", starter: "Priority tickets", pro: "Dedicated" },
    { name: "Documents", free: true, starter: true, pro: true },
    { name: "Sticky Notes", free: true, starter: true, pro: true },
    { name: "Reminders", free: true, starter: true, pro: true },
    { name: "Webhooks", free: true, starter: true, pro: true },
    { name: "Calendar Sync", free: true, starter: true, pro: true },
    { name: "Canvas Integration", free: true, starter: true, pro: true },
    { name: "Classroom Sync", free: true, starter: true, pro: true },
    { name: "Spotify Player*", free: true, starter: true, pro: true },
    { name: "Appointments*", free: false, starter: true, pro: true },
    { name: "API Access*", free: false, starter: true, pro: true },
    { name: "Public Forms", free: false, starter: true, pro: true },
    { name: "SSO*", free: false, starter: false, pro: true },
    { name: "Custom Development", free: false, starter: false, pro: true },
  ];

  const displayedFeatures = showAllFeatures ? allFeatures : allFeatures.slice(0, 10);

  return <section id="pricing" className="py-24 bg-background">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            Simple, transparent
            <span className="bg-gradient-hero bg-clip-text text-transparent"> pricing</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Choose the plan that fits your team's needs. No hidden fees, cancel anytime.
          </p>
        </div>

        {/* Desktop: 3 columns, Tablet: stacked horizontal cards, Mobile: single column */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <Card 
              key={index} 
              className={`relative transition-all duration-300 hover:-translate-y-2 ${
                plan.popular ? 'border-primary shadow-glow bg-gradient-glass backdrop-blur-sm' : 'hover:shadow-medium bg-card'
              } p-8 flex flex-col h-full`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium shadow-soft">
                    Most Popular
                  </div>
                </div>
              )}

              {/* Header Section */}
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-foreground mb-2">{plan.name}</h3>
                <p className="text-muted-foreground mb-4">{plan.description}</p>
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-bold text-foreground">{plan.price}</span>
                  <span className="text-muted-foreground ml-1">{plan.period}</span>
                </div>
              </div>

              {/* Features Section - Single line layout */}
              <div className="flex-1 mb-6">
                <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center">
                  {plan.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-center gap-2 whitespace-nowrap">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA Section */}
              <div className="mt-auto">
                <Button 
                  variant={plan.variant} 
                  size="lg" 
                  className="w-full gap-2 group"
                  onClick={() => plan.cta === "Sign Up" ? navigate("/auth?mode=signup") : undefined}
                >
                  {plan.cta}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </Card>
          ))}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-4">*coming soon</p>

        {/* Feature Comparison Table */}
        <div className="mt-16 max-w-6xl mx-auto">
          <h3 className="text-2xl font-bold text-foreground text-center mb-8">Compare Features</h3>
          <div className="bg-card border rounded-lg overflow-hidden shadow-soft">
            <Table>
              <TableHeader>
                <TableRow className="border-b bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5">
                  <TableHead className="w-1/4 font-semibold text-foreground">Features</TableHead>
                  <TableHead className="text-center font-semibold text-foreground">Free</TableHead>
                  <TableHead className="text-center font-semibold text-foreground bg-primary/10 border-l border-r border-primary/20">Starter</TableHead>
                  <TableHead className="text-center font-semibold text-foreground">Professional</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedFeatures.map((feature, index) => (
                  <TableRow key={index} className="hover:bg-muted/30 transition-colors">
                    <TableCell className="font-medium">{feature.name}</TableCell>
                    <TableCell className="text-center">
                      {typeof feature.free === 'boolean' ? (
                        feature.free ? (
                          <Check className="w-4 h-4 text-primary mx-auto" />
                        ) : (
                          <X className="w-4 h-4 text-muted-foreground mx-auto" />
                        )
                      ) : (
                        feature.free
                      )}
                    </TableCell>
                    <TableCell className="text-center bg-primary/5 border-l border-r border-primary/10">
                      {typeof feature.starter === 'boolean' ? (
                        feature.starter ? (
                          <Check className="w-4 h-4 text-primary mx-auto" />
                        ) : (
                          <X className="w-4 h-4 text-muted-foreground mx-auto" />
                        )
                      ) : (
                        feature.starter
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {typeof feature.pro === 'boolean' ? (
                        feature.pro ? (
                          <Check className="w-4 h-4 text-primary mx-auto" />
                        ) : (
                          <X className="w-4 h-4 text-muted-foreground mx-auto" />
                        )
                      ) : (
                        feature.pro
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="flex justify-center mt-6">
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => setShowAllFeatures(!showAllFeatures)}
              className="gap-2"
            >
              {showAllFeatures ? "Show less" : "Show all features"}
              <ChevronDown className={`w-4 h-4 transition-transform ${showAllFeatures ? 'rotate-180' : ''}`} />
            </Button>
          </div>
        </div>

      </div>
    </section>;
};